<template>
  <div class="app-layout">
    <Sidebar />
    <div class="course-categories">
      <div class="content-wrapper">
        <div class="page-header">
          <h2 class="section-title">Course Categories</h2>
          
          <!-- Search input -->
          <div class="search-container">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search courses..." 
              class="search-input"
            >
            <span v-if="searchQuery" @click="clearSearch" class="search-clear">×</span>
          </div>
        </div>
        
        <!-- Loading state -->
        <div v-if="loading" class="loading">
          <div class="loading-spinner"></div>
          Loading categories...
        </div>
        
        <!-- Error state -->
        <div v-else-if="error" class="error">
          {{ error }}
        </div>
        
        <!-- Categories list -->
        <div v-else class="categories-list">
          <!-- Show message when no search results -->
          <div v-if="searchQuery && !hasSearchResults" class="no-results">
            No courses found matching "{{ searchQuery }}"
          </div>
          
          <div v-for="category in filteredCategories" :key="category.id" class="category-card" :class="{ 'uncategorized': category.is_uncategorized }">
            <div class="category-header">
              <div class="category-titles">
                <h3 class="category-title">{{ category.title }}</h3>
                <p class="category-title-ar">{{ category.title_ar }}</p>
              </div>
              <div class="category-stats">
                <span class="course-count">{{ category.courses?.length || 0 }} Courses</span>
              </div>
            </div>
            
            <!-- Courses within category -->
            <div class="courses-list">
              <div v-for="course in category.courses" :key="course.id" class="course-item">
                <div class="course-content">
                  <div class="course-header">
                    <div class="course-title-wrapper">
                      <h4 class="course-title">{{ course.title }}</h4>
                      <p class="course-title-ar">{{ course.title_ar }}</p>
                    </div>
                    <div class="course-actions">
                      <button 
                        v-if="!category.is_uncategorized"
                        @click="confirmRemove(course.id, category.id)" 
                        class="remove-button" 
                        title="Remove from this category"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="icon">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                      <input 
                        type="checkbox" 
                        :value="course.id" 
                        v-model="selectedCourses"
                        class="course-checkbox"
                      >
                    </div>
                  </div>
                  <div class="course-details">
                    <div class="course-info">
                      <span class="course-duration">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {{ course.minutes }} minutes
                      </span>
                      <span class="course-status" :class="course.status">
                        <span class="status-dot"></span>
                        {{ course.status }}
                      </span>
                    </div>
                    <div class="course-price">{{ formatPrice(course.price) }}</div>
                  </div>
                  <div class="course-badges">
                    <span v-if="course.is_premium" class="badge premium">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="icon">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                      </svg>
                      Premium
                    </span>
                    <span v-if="course.top_course" class="badge top">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="icon">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                      Top Course
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Move Courses Action Panel -->
        <div v-if="selectedCourses.length > 0" class="action-panel">
          <div class="selected-count">
            {{ selectedCourses.length }} courses selected
          </div>
          <div class="move-controls">
            <div class="action-options">
              <label class="action-type">
                <input type="radio" v-model="actionType" value="move" name="actionType">
                Move to category
              </label>
              <label class="action-type">
                <input type="radio" v-model="actionType" value="add" name="actionType">
                Add to categories
              </label>
            </div>
            
            <!-- Single category selection (for move) -->
            <select v-if="actionType === 'move'" v-model="targetCategoryId" class="category-select">
              <option value="">Select target category</option>
              <option 
                v-for="category in categories" 
                :key="category.id" 
                :value="category.id"
              >
                {{ category.title }}
              </option>
            </select>
            
            <!-- Multiple categories selection (for add) -->
            <div v-if="actionType === 'add'" class="multi-select-container">
              <div class="selected-categories" v-if="selectedCategories.length > 0">
                <span v-for="catId in selectedCategories" :key="catId" class="selected-category">
                  {{ getCategoryName(catId) }}
                  <button @click="removeCategory(catId)" class="remove-category">×</button>
                </span>
              </div>
              <select v-model="tempCategoryId" class="category-select" @change="addCategory">
                <option value="">Add categories...</option>
                <option 
                  v-for="category in availableCategories" 
                  :key="category.id" 
                  :value="category.id"
                >
                  {{ category.title }}
                </option>
              </select>
            </div>
            
            <button 
              @click="actionType === 'move' ? moveCourses() : addToCategories()" 
              :disabled="!canProceed || processing" 
              class="action-button"
            >
              {{ processing ? 'Processing...' : actionType === 'move' ? 'Move Courses' : 'Add to Categories' }}
            </button>
            <button @click="clearSelection" class="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmation" class="modal-overlay">
      <div class="confirmation-modal">
        <h3>Remove Course</h3>
        <p>Are you sure you want to remove this course from the category?</p>
        <div class="modal-actions">
          <button @click="removeFromCategory" class="confirm-button" :disabled="removing">
            {{ removing ? 'Removing...' : 'Yes, Remove' }}
          </button>
          <button @click="cancelRemove" class="cancel-button">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'CourseCategories',
  components: {
    Sidebar
  },
  setup() {
    const categories = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedCourses = ref([]);
    const targetCategoryId = ref('');
    const moving = ref(false);
    const searchQuery = ref('');
    const actionType = ref('move');
    const selectedCategories = ref([]);
    const tempCategoryId = ref('');
    const processing = ref(false);
    const showConfirmation = ref(false);
    const pendingRemoval = ref({ courseId: null, categoryId: null });
    const removing = ref(false);

    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/courses/courses/categories', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          // Transform the data structure - group courses by categories
          const categoriesMap = new Map();
          const allCourses = new Map(); // To track all courses by ID
          
          // First, collect all courses and mark them as uncategorized initially
          response.data.data.courses.forEach(course => {
            allCourses.set(course.id, { ...course, hasCategory: false });
          });
          
          // Collect all unique categories from the courses
          response.data.data.courses.forEach(course => {
            course.categories.forEach(category => {
              if (!categoriesMap.has(category.id)) {
                categoriesMap.set(category.id, {
                  ...category,
                  courses: []
                });
              }
            });
          });
          
          // Then, add each course to all its categories
          response.data.data.courses.forEach(course => {
            if (course.categories && course.categories.length > 0) {
              course.categories.forEach(category => {
                categoriesMap.get(category.id).courses.push(course);
                allCourses.get(course.id).hasCategory = true; // Mark as categorized
              });
            }
          });
          
          // Find uncategorized courses
          const uncategorizedCourses = [];
          allCourses.forEach(course => {
            if (!course.hasCategory) {
              uncategorizedCourses.push(course);
            }
          });
          
          // Add a special "uncategorized" pseudo-category if there are uncategorized courses
          if (uncategorizedCourses.length > 0) {
            categoriesMap.set('uncategorized', {
              id: 'uncategorized',
              title: 'Uncategorized Courses',
              title_ar: 'دورات غير مصنفة',
              courses: uncategorizedCourses,
              is_uncategorized: true
            });
          }
          
          // Convert map to array
          categories.value = Array.from(categoriesMap.values());
        } else {
          throw new Error('Failed to fetch categories');
        }
      } catch (err) {
        error.value = 'Error loading categories: ' + (err.message || 'Unknown error');
      } finally {
        loading.value = false;
      }
    };

    const getCategoryName = (categoryId) => {
      const category = categories.value.find(cat => cat.id === categoryId);
      return category ? category.title : '';
    };

    const addCategory = () => {
      if (tempCategoryId.value && !selectedCategories.value.includes(tempCategoryId.value)) {
        selectedCategories.value.push(tempCategoryId.value);
      }
      tempCategoryId.value = '';
    };

    const removeCategory = (categoryId) => {
      selectedCategories.value = selectedCategories.value.filter(id => id !== categoryId);
    };

    const availableCategories = computed(() => {
      return categories.value.filter(category => 
        !selectedCategories.value.includes(category.id)
      );
    });

    const canProceed = computed(() => {
      if (actionType.value === 'move') {
        return targetCategoryId.value && selectedCourses.value.length > 0;
      } else {
        return selectedCategories.value.length > 0 && selectedCourses.value.length > 0;
      }
    });

    const moveCourses = async () => {
      if (!targetCategoryId.value || selectedCourses.value.length === 0) return;
      
      processing.value = true;
      try {
        const token = localStorage.getItem('token');
        await axios.post('https://api.kewaar.com/api/admin/courses/category/move-bulk', {
          course_ids: selectedCourses.value,
          category_id: targetCategoryId.value
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Refresh categories after move
        await fetchCategories();
        clearSelection();
      } catch (err) {
        error.value = 'Error moving courses: ' + (err.message || 'Unknown error');
      } finally {
        processing.value = false;
      }
    };

    const addToCategories = async () => {
      if (selectedCategories.value.length === 0 || selectedCourses.value.length === 0) return;
      
      processing.value = true;
      try {
        const token = localStorage.getItem('token');
        await axios.post('https://api.kewaar.com/api/admin/courses/add-to-categories', {
          course_ids: selectedCourses.value,
          category_ids: selectedCategories.value
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Refresh categories after adding
        await fetchCategories();
        clearSelection();
      } catch (err) {
        error.value = 'Error adding courses to categories: ' + (err.message || 'Unknown error');
      } finally {
        processing.value = false;
      }
    };

    const clearSelection = () => {
      selectedCourses.value = [];
      targetCategoryId.value = '';
      selectedCategories.value = [];
      tempCategoryId.value = '';
    };

    const formatPrice = (price) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(price);
    };

    const filteredCategories = computed(() => {
      if (!searchQuery.value.trim()) {
        return categories.value;
      }
      
      const query = searchQuery.value.toLowerCase().trim();
      
      return categories.value.map(category => {
        // If it's the uncategorized category, filter its courses
        if (category.is_uncategorized) {
          const filteredUncategorizedCourses = category.courses?.filter(course => 
            course.title.toLowerCase().includes(query) || 
            (course.title_ar && course.title_ar.toLowerCase().includes(query))
          ) || [];
          
          // Return a copy of the category with filtered courses
          return {
            ...category,
            courses: filteredUncategorizedCourses
          };
        }
        
        // For regular categories, filter as before
        const filteredCourses = category.courses?.filter(course => 
          course.title.toLowerCase().includes(query) || 
          (course.title_ar && course.title_ar.toLowerCase().includes(query))
        ) || [];
        
        return {
          ...category,
          courses: filteredCourses
        };
      }).filter(category => 
        // Keep the uncategorized category if it has matching courses
        (category.is_uncategorized && category.courses.length > 0) || 
        // Keep regular categories with matching courses
        (!category.is_uncategorized && category.courses.length > 0)
      );
    });
    
    const hasSearchResults = computed(() => {
      return filteredCategories.value.some(category => category.courses.length > 0);
    });
    
    const clearSearch = () => {
      searchQuery.value = '';
    };

    const confirmRemove = (courseId, categoryId) => {
      pendingRemoval.value = { courseId, categoryId };
      showConfirmation.value = true;
    };

    const cancelRemove = () => {
      showConfirmation.value = false;
      pendingRemoval.value = { courseId: null, categoryId: null };
    };

    const removeFromCategory = async () => {
      if (!pendingRemoval.value.courseId || !pendingRemoval.value.categoryId) return;
      
      removing.value = true;
      try {
        const token = localStorage.getItem('token');
        await axios.post('https://api.kewaar.com/api/admin/courses/remove-from-category', {
          course_id: pendingRemoval.value.courseId,
          category_id: pendingRemoval.value.categoryId
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Refresh categories after removal
        await fetchCategories();
        showConfirmation.value = false;
        pendingRemoval.value = { courseId: null, categoryId: null };
      } catch (err) {
        error.value = 'Error removing course from category: ' + (err.message || 'Unknown error');
      } finally {
        removing.value = false;
      }
    };

    onMounted(() => {
      fetchCategories();
    });

    return {
      categories,
      loading,
      error,
      formatPrice,
      selectedCourses,
      targetCategoryId,
      moving,
      processing,
      moveCourses,
      clearSelection,
      searchQuery,
      filteredCategories,
      hasSearchResults,
      clearSearch,
      actionType,
      selectedCategories,
      tempCategoryId,
      addCategory,
      removeCategory,
      getCategoryName,
      availableCategories,
      canProceed,
      addToCategories,
      showConfirmation,
      pendingRemoval,
      removing,
      confirmRemove,
      cancelRemove,
      removeFromCategory
    };
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background: white;
}

.course-categories {
  flex: 1;
  margin-left: 280px; /* Add margin to prevent overlap with sidebar */
  background: white;
  color: #1a1f2c;
}

.content-wrapper {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1f2c;
  margin: 0;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  font-size: 1.1rem;
  color: #64B5F6;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(100, 181, 246, 0.2);
  border-top-color: #64B5F6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  padding: 1rem;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  color: #dc3545;
  border-radius: 8px;
}

.categories-list {
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
}

.category-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.category-header {
  padding: 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-titles {
  flex: 1;
}

.category-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1f2c;
}

.category-title-ar {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 1rem;
}

.category-stats {
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #64748b;
}

.courses-list {
  padding: 1.5rem;
  display: grid;
  gap: 1rem;
}

.course-item {
  padding: 1.25rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.course-item:hover {
  transform: translateY(-2px);
  background: #f8fafc;
  border-color: #e2e8f0;
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.course-title-wrapper {
  flex: 1;
}

.course-actions {
  display: flex;
  align-items: center;
}

.course-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-left: 1rem;
}

.course-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: #1a1f2c;
}

.course-title-ar {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.9rem;
}

.course-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.course-info {
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
  color: #64748b;
}

.course-duration, .course-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon {
  width: 16px;
  height: 16px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2e7d32;
}

.course-status.active .status-dot {
  background: #10b981;
}

.course-price {
  font-weight: 600;
  color: #64B5F6;
}

.course-badges {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
}

.badge {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.badge .icon {
  width: 14px;
  height: 14px;
}

.badge.premium {
  background: #fff7ed;
  color: #ea580c;
}

.badge.top {
  background: #f0f9ff;
  color: #0284c7;
}

.action-panel {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 280px;
  background: white;
  padding: 1rem 2rem;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
}

.selected-count {
  font-weight: 500;
  color: #1a1f2c;
}

.move-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.category-select {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 200px;
}

.move-button, .cancel-button {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.move-button {
  background: #0284c7;
  color: white;
  border: none;
}

.move-button:hover:not(:disabled) {
  background: #0369a1;
}

.move-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-button {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.cancel-button:hover {
  background: #f8fafc;
}

.action-options {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.action-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
}

.multi-select-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 250px;
}

.selected-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.selected-category {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.6rem;
  background: #f0f9ff;
  color: #0284c7;
  border-radius: 4px;
  font-size: 0.8rem;
}

.remove-category {
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
}

.action-button {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #0284c7;
  color: white;
  border: none;
}

.action-button:hover:not(:disabled) {
  background: #0369a1;
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.6rem 1rem;
  padding-right: 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #64B5F6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(100, 181, 246, 0.2);
}

.search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.25rem;
  color: #64748b;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-results {
  grid-column: 1/-1;
  padding: 2rem;
  text-align: center;
  background: #f8fafc;
  border-radius: 12px;
  color: #64748b;
  border: 1px dashed #e2e8f0;
}

@media (max-width: 1200px) {
  .categories-list {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}

@media (max-width: 768px) {
  .course-categories {
    margin-left: 0; /* Remove margin on mobile when sidebar is hidden */
  }
  
  .content-wrapper {
    padding: 1rem;
  }

  .categories-list {
    grid-template-columns: 1fr;
  }

  .course-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .course-price {
    margin-top: 0.5rem;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-container {
    width: 100%;
  }
}

/* New styles for delete functionality */
.remove-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  margin-right: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.remove-button:hover {
  background: rgba(239, 68, 68, 0.1);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirmation-modal {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.confirmation-modal h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 1rem;
}

.confirmation-modal p {
  margin-bottom: 1.5rem;
  color: #64748b;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.confirm-button {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  background: #ef4444;
  color: white;
  border: none;
  transition: all 0.2s ease;
}

.confirm-button:hover:not(:disabled) {
  background: #dc2626;
}

.confirm-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.category-card.uncategorized {
  border: 1px dashed #ef4444;
  background-color: rgba(239, 68, 68, 0.03);
}

.category-card.uncategorized .category-header {
  background-color: rgba(239, 68, 68, 0.1);
  border-bottom: 1px dashed #ef4444;
}

.category-card.uncategorized .category-title {
  color: #ef4444;
}

.category-card.uncategorized .category-stats {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}
</style>